import { useAuthStoreV1 } from '@/stores/authStore_v1';
import { useShallow } from 'zustand/react/shallow';
import { cn } from '@/lib/utils';
import {
  connectionHost,
  environmentLabel,
  isProductionEnvironment,
} from './connectionEnvironment';

/**
 * What this Admin Panel is actually administering.
 *
 * THE PROBLEM. An "organization" in this product is not the customer's company — it is one connected
 * Salesforce environment. `Organization` carries `salesforceOrgId`, `instanceUrl`, `orgType` and
 * `metadataTypeCounts`; a user holds many, and the product advertises no cap on how many you connect.
 * Every concept in this panel is scoped to exactly one of them: its own member roster, its own role
 * catalogue, its own invitations, its own Stripe billing scope, its own budget ledger. There is no
 * company-level entity anywhere in the frontend, so nothing can legitimately roll them up.
 *
 * The header printed only `orgName`. So an admin with Production plus two sandboxes had no way to
 * see which of three separate worlds they were about to change a plan in or deactivate a leaver
 * from — and the sidebar switcher repoints all of it silently. Nothing about a name distinguishes
 * "my company" from "one sandbox of my company".
 *
 * WHY PRODUCTION IS THE ONE THAT SHOUTS. The damaging mistake is believing you are in a sandbox
 * while changing production, so production is the state that has to announce itself; a marker that
 * appeared only on sandboxes would teach the reader to infer production from an ABSENCE, which is
 * the inference that fails when the chip is missing for any other reason. Both states are therefore
 * always rendered, and production gets the solid one.
 *
 * The chip is authoritative, not alarming: production is the normal, correct place to be doing this
 * work. It uses the foreground/background inversion rather than a warning colour, because this
 * module reserves warning and destructive tones for money problems.
 *
 * Pure helpers live in `connectionEnvironment.ts` so they can be unit-tested without a DOM. NOT
 * `connectionScope.ts`: two modules whose names differ only in case resolve to whichever the bundler
 * reaches first on a case-insensitive filesystem, and rspack picked the `.ts` — so every export of
 * this file silently disappeared from the bundle while `tsc` stayed green.
 */

export { connectionHost, environmentLabel, isProductionEnvironment };

export function EnvironmentChip({
  orgType,
  className,
}: {
  orgType: string | null | undefined;
  className?: string;
}) {
  const label = environmentLabel(orgType);
  if (!label) return null;

  return (
    <span
      className={cn(
        /*
         * 11px, matching `--oa-fs-micro` — the module's smallest legitimate size, and the floor the
         * admin sweep enforces. It was 10px, which the sweep failed on ("text under 11px") the
         * moment the QA fixture gained a real `orgType`. Written as explicit utilities rather than
         * by applying `.oa-micro`: that class also sets a muted colour at the same specificity as a
         * Tailwind colour utility, so the production chip's inverted text would be a cascade
         * coin-toss. Same values, no fight.
         */
        'inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-[11px] font-semibold uppercase leading-tight tracking-[0.06em]',
        isProductionEnvironment(orgType)
          ? 'bg-foreground text-background'
          : 'border border-border/80 text-muted-foreground',
        className,
      )}
    >
      {label}
    </span>
  );
}

export interface ConnectionScopeResult {
  name: string | null;
  orgType: string | null;
  host: string | null;
}

/** The selected connection's identity, read straight from the single source of truth. */
export function useConnectionScope(): ConnectionScopeResult {
  const { name, orgType, instanceUrl } = useAuthStoreV1(
    useShallow((s) => ({
      name: s.selectedOrg?.orgName ?? null,
      orgType: s.selectedOrg?.orgType ?? null,
      instanceUrl: s.selectedOrg?.instanceUrl ?? null,
    })),
  );
  return { name, orgType, host: connectionHost(instanceUrl) };
}

/**
 * The scope line: which connection, which environment, which Salesforce instance.
 *
 * Wraps rather than truncating as a whole, so the environment chip is never the part that gets cut —
 * it is the highest-value token on the line.
 */
export function ConnectionScopeLine({ className }: { className?: string }) {
  const { name, orgType, host } = useConnectionScope();

  return (
    <div className={cn('flex flex-wrap items-center gap-x-2 gap-y-1', className)}>
      <span className="oa-meta min-w-0">
        {/* Administering{' '} */}
        <span className="font-medium text-foreground">{name ?? 'this connection'}</span>
      </span>
      <EnvironmentChip orgType={orgType} />
      {host && (
        /* `title` carries the full host: the visible copy truncates at narrow panel widths, and a
           truncated hostname is worse than none for telling two connections apart. */
        <span className="oa-meta min-w-0 truncate" title={host}>
          · {host}
        </span>
      )}
    </div>
  );
}
